import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import {
    ContentBlock,
    OneLinkProps
} from "@/components/wpl/content-block/src/content-block";
import { Link } from "@/libraries/design/components";
import { Row } from "@/libraries/wpl/foundations/src/grid";
import { IconNameTypes } from "@/libraries/wpl/foundations/src/icon";
import {
    BGProps,
    StyledBackground
} from "@/libraries/wpl/foundations/src/styled-backgrounds";

export interface InlineCardProps {
    headerText: string;
    bodyText: string;
    imageLink: string;
    imageAltText: string;
    ctaProps?: OneLinkProps;
}

export interface PatternInlineContentProps {
    headerText: string;
    bodyText: string;
    eyebrowText?: string;
    iconName?: IconNameTypes;
    className?: string;
    ctaProps?: OneLinkProps;
    linkPosition?: "top" | "bottom";
    inlineContentBlocks: InlineCardProps[];
    backgroundProperties?: BGProps;
}

const getCardAspectRatio = (length: number) => {
    switch (length) {
        case 4:
            return "4/3";
        case 3:
            return "4/3";
        case 2:
            return "2/1";
        default:
            return "1/1";
    }
};

const ctaStyle = css({
    [media.above.sm]: { padding: "64px 0" },
    [media.only.sm]: {
        padding: "48px 0"
    },
    [media.below.sm]: {
        padding: "32px 0"
    }
});

export const ContentBlocksInline = ({
    headerText,
    bodyText,
    backgroundProperties,
    className,
    eyebrowText,
    iconName,
    linkPosition = "bottom",
    ctaProps,
    inlineContentBlocks
}: PatternInlineContentProps) => {
    if (inlineContentBlocks.length < 2 || inlineContentBlocks.length > 4) {
        throw new Error("Pattern is meant to display between 2 and 4 cards");
    }
    return (
        <StyledBackground {...backgroundProperties}>
            <div className={className} css={ctaStyle}>
                <Row>
                    <ContentBlock
                        headerText={headerText}
                        headerType="h2"
                        eyebrowText={eyebrowText}
                        iconName={iconName}
                        bodyText={bodyText}
                        isCentered={true}
                        ctaProps={
                            linkPosition === "top" && ctaProps?.primaryLinkProps
                                ? ctaProps
                                : {}
                        }
                    />
                </Row>

                <Row>
                    {inlineContentBlocks.map((block, index) => (
                        <ContentBlock
                            key={index}
                            aspectRatio={getCardAspectRatio(
                                inlineContentBlocks.length
                            )}
                            {...block}
                        />
                    ))}
                </Row>

                {linkPosition === "bottom" && ctaProps?.primaryLinkProps ? (
                    <Row>
                        <div style={{ textAlign: "center" }}>
                            {/* eslint-disable-next-line @atlassian/a11y/anchor-has-content */}
                            <Link {...ctaProps.primaryLinkProps} />
                        </div>
                    </Row>
                ) : null}
            </div>
        </StyledBackground>
    );
};
